import { useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import { MarkdownLite, TeachingVideo } from "@/components/TeachingTileCard";
import { teachingNotes } from "@/lib/teaching-materials";
import { TeachingMaterials } from "@/components/learn/TeachingMaterials";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import {
  containsPatientIdentifier,
  todayIsoDate,
  useBoardLearn,
  useCreateCpdEntry,
  useMarkTileViewed,
  type BoardTeachingTile,
} from "@/lib/board";
import { allowedVideoEmbedUrl } from "@/lib/learn";
import { FiveMinuteCard } from "./BoardLearn";
import "@/styles/five-minute-cpd.css";

export default function BoardLearnTile() {
  const { id = "" } = useParams<{ id: string }>();
  const { city } = useCity();
  const learn = useBoardLearn(city.slug);
  const tile = learn.data?.tiles.find((item) => item.id === id);
  const markViewed = useMarkTileViewed(city.slug, id);
  const createCpd = useCreateCpdEntry(city.slug);
  const trackedId = useRef<string | null>(null);
  useEffect(() => {
    if (!tile || trackedId.current === tile.id) return;
    trackedId.current = tile.id;
    track("tile_view", { tile_id: tile.id });
  }, [tile]);
  if (!id) return <Navigate to="/board/learn" replace />;
  if (learn.isLoading)
    return (
      <div className="pb-board-state" role="status">
        Loading session…
      </div>
    );
  if (learn.isError || !tile)
    return (
      <div className="pb-board-state pb-board-state--error" role="alert">
        This teaching session is unavailable.{" "}
        <button type="button" onClick={() => void learn.refetch()}>
          Retry
        </button>{" "}
        <Link to="/board/learn">All sessions</Link>
      </div>
    );
  return (
    <FiveMinuteSession
      key={tile.id}
      tile={tile}
      related={(learn.data?.tiles ?? [])
        .filter(
          (item) =>
            item.id !== tile.id && item.specialtySlug === tile.specialtySlug,
        )
        .slice(0, 3)}
      saving={markViewed.isPending || createCpd.isPending}
      error={markViewed.isError || createCpd.isError}
      onComplete={() =>
        markViewed.mutate(undefined, {
          onSuccess: () => track("learn_watch", { tile_id: tile.id }),
        })
      }
      onRecord={(minutes, note) =>
        createCpd.mutate(
          {
            tileId: tile.id,
            activityType: "educational",
            minutes,
            note,
            title: tile.title,
            loggedOn: todayIsoDate(),
          },
          {
            onSuccess: () =>
              track("cpd_logged", { activity_type: "educational", minutes }),
          },
        )
      }
    />
  );
}

export function FiveMinuteSession({
  tile,
  related,
  saving,
  error,
  onComplete,
  onRecord,
}: {
  tile: BoardTeachingTile;
  related: BoardTeachingTile[];
  saving: boolean;
  error: boolean;
  onComplete: () => void;
  onRecord: (minutes: number, note: string) => void;
}) {
  const [minutes, setMinutes] = useState("");
  const [note, setNote] = useState("");
  const [validation, setValidation] = useState("");
  const embedUrl = allowedVideoEmbedUrl(tile.videoUrl);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const actualMinutes = Number(minutes);
    if (
      !Number.isInteger(actualMinutes) ||
      actualMinutes < 1 ||
      actualMinutes > 480
    ) {
      setValidation(
        "Enter the actual time you spent learning, from 1 to 480 whole minutes.",
      );
      return;
    }
    if (containsPatientIdentifier(note)) {
      setValidation("Remove patient identifiers from your reflection.");
      return;
    }
    setValidation("");
    onRecord(actualMinutes, note.trim());
  };
  return (
    <article className="pb-five pb-board-learn-detail">
      <Link className="pb-board-back" to="/board/learn">
        <ArrowLeft size={15} aria-hidden="true" />
        Five Minute CPD
      </Link>
      <header>
        <p className="pb-board-eyebrow">
          {tile.specialtyName} ·{" "}
          {embedUrl ? "Video session" : "Reading session"}
        </p>
        <h1>{tile.title}</h1>
        <p>
          {tile.specialist.name}
          {tile.specialist.practice ? ` · ${tile.specialist.practice}` : ""}
        </p>
      </header>
      <div className="pb-session-theatre"><div className="pb-session-video">
      {embedUrl ? (
        <TeachingVideo embedUrl={embedUrl} title={tile.title} />
      ) : tile.videoUrl ? (
        <p role="status">
          This video address is not supported. The session notes remain
          available below.
        </p>
      ) : null}
      </div><TeachingMaterials body={tile.body} /></div>
      <section aria-label="Session notes">
        <MarkdownLite body={teachingNotes(tile.body)} />
      </section>
      <section className="pb-five-record" aria-labelledby="record-heading">
        <h2 id="record-heading">Make it part of your practice</h2>
        <p>
          What will you take into your next consultation? Record the time you
          actually spent learning. This is a personal, self-reported record, not
          verified attendance or accredited CPD.
        </p>
        {tile.cpdLogged ? (
          <p role="status">
            <CheckCircle2 size={16} aria-hidden="true" /> Learning recorded.{" "}
            <Link to="/board/cpd">View or export your CPD log</Link>
          </p>
        ) : !tile.completed ? (
          <>
            <p>
              When you have finished {embedUrl ? "watching" : "reading"},
              confirm completion to add a learning entry.
            </p>
            <button
              className="pb-board-primary"
              type="button"
              disabled={saving}
              onClick={onComplete}
            >
              {saving
                ? "Saving…"
                : `I have finished ${embedUrl ? "watching" : "reading"}`}
            </button>
          </>
        ) : (
          <form onSubmit={submit}>
            <label>
              Actual learning time (minutes)
              <input
                type="number"
                min={1}
                max={480}
                step={1}
                required
                value={minutes}
                onChange={(event) => setMinutes(event.target.value)}
                placeholder="e.g. 5"
              />
            </label>
            <label>
              One thing I will take into practice (optional)
              <textarea
                maxLength={200}
                rows={3}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="A question to ask, a point to check, or something to discuss with a colleague."
                aria-describedby="reflection-privacy"
              />
            </label>
            <p id="reflection-privacy">
              Keep reflections general. Do not include patient names, dates of
              birth, case details or other identifiers. A reflection here
              remains part of your educational entry; it is not automatically
              Reviewing Performance credit.
            </p>
            <button
              className="pb-board-primary"
              type="submit"
              disabled={saving}
            >
              {saving ? "Saving…" : "Save learning record"}
            </button>
          </form>
        )}
        {validation || error ? (
          <p className="pb-board-form-error" role="alert">
            {validation || "Your record could not be saved. Please try again."}
          </p>
        ) : null}
      </section>
      <aside className="pb-five-cpd-note">
        <p>
          Check eligibility and recording requirements with your CPD home.
          PulseBoard does not award points or automatically submit hours.
          Viewing alone does not fulfil all CPD requirements.
        </p>
      </aside>
      {related.length ? (
        <section className="pb-five-related">
          <h2>More in {tile.specialtyName.toLowerCase()}</h2>
          <div className="pb-five-grid">
            {related.map((item) => (
              <FiveMinuteCard key={item.id} tile={item} />
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
}
